import {
  ActivityFilterModel,
  ActivityOrderBy,
  FeatureName,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { IssueCardData } from '../activity/issueCard';
import { IssueCardCompact } from '../activity/issueCardCompact';
import { tokens } from '../crop-circle/tokens';
import { PinnedActivitiesOld } from './pinnedActivities-old';

type PinnedActivitiesProps = {
  assetId: string;
  pinnedActivities: IssueCardData[];
  showAll?: boolean;
  onViewAllPress?: () => void;
  onActivityPress?: (activityId: string) => void;
};

const pinnedFilter = new ActivityFilterModel({
  orderBy: ActivityOrderBy.MostRecentUpdated,
});

export const PinnedActivities = (props: PinnedActivitiesProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <PinnedActivitiesBase {...props} />;
  }

  return <PinnedActivitiesOld {...props} />;
};

const PinnedActivitiesBase = ({
  pinnedActivities,
  showAll = false,
  onViewAllPress,
  onActivityPress,
}: PinnedActivitiesProps) => {
  const { t } = useTranslation();

  if (!pinnedActivities || pinnedActivities.length === 0) {
    return null;
  }

  const displayedActivities = showAll
    ? pinnedActivities
    : pinnedActivities.slice(0, 3);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[tokens.typography.label.largeEmphasised, styles.title]}>
          {t('asset.pinnedActivity')}
        </Text>
        {!showAll && pinnedActivities.length > 3 && (
          <Pressable onPress={onViewAllPress}>
            <Text style={styles.viewAllText}>{t('asset.viewAll')}</Text>
          </Pressable>
        )}
      </View>

      <View style={styles.activitiesList}>
        {displayedActivities.map((activity) => (
          <Pressable
            key={activity.id}
            onPress={() => onActivityPress?.(activity.id)}
          >
            <IssueCardCompact
              account={null}
              activity={activity}
              isCropCircleRedesignEnabled={true}
              filter={pinnedFilter}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    padding: 16,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 0,
    marginBottom: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontWeight: '600',
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.slate[400],
    lineHeight: 18,
  },
  activitiesList: {
    flexDirection: 'column',
    gap: 4,
  },
});
